import { c as _c } from "react/compiler-runtime";
import { Form, Formik, useFormikContext } from 'formik';
import { useContext } from 'react';
import useSWR from 'swr';
import { OutpostDestinationCreateInput, OutpostDestinationTypeMetadata } from '../../../../../../../../typings/outpost';
import APIMethodKeys from '../../../../../client/APIMethodKeys';
import { capitalizeFirstLetter } from '../../../../../utils';
import Badge from '../../../../common/base/Badge';
import { StyledCard, StyledCardSection } from '../../../../common/base/Card';
import Link from '../../../../common/base/Link';
import Loading from '../../../../common/base/Loading';
import Text from '../../../../common/base/Text';
import { CodeSnippetWithForm } from '../../../../common/CodeSnippet';
import { Div } from '../../../../common/helpers/StyledUtils';
import { GlobalContext } from '../../../../contexts/GlobalContext';
import { DestinationCreateFields, DestinationFormValues, getCreateDestinationInitialValues, getCreateDestinationInput, noopOutpostFormSubmit } from '../../utils/outpostForms';
import { buildCreateDestinationExamples, getOutpostSnippetLanguage, useOutpostSnippetMaskableValue } from '../../utils/outpostSnippets';
import { DEFAULT_TEST_DESTINATION_URL, OutpostGetStartedContext, OutpostGetStartedStepProps } from '../GetStartedView';
import CreatedResourceData from './CreatedResourceData';
const CreateDestinationForm: React.FC<{
  context: OutpostGetStartedContext;
  destination_types: OutpostDestinationTypeMetadata[];
}> = t0 => {
  const $ = _c(26);
  const {
    context: t1,
    destination_types
  } = t0;
  const {
    active_language,
    tenant,
    destination,
    topics
  } = t1;
  const {
    values
  } = useFormikContext();
  let t2;
  if ($[0] !== topics) {
    t2 = topics?.map(_temp) ?? [];
    $[0] = topics;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const topic_options = t2;
  let t3;
  if ($[2] !== active_language || $[3] !== destination_types || $[4] !== tenant?.id || $[5] !== values) {
    const create_data = getCreateDestinationInput({
      destination_types,
      values
    }) as OutpostDestinationCreateInput;
    t3 = buildCreateDestinationExamples(tenant?.id ?? "", create_data)[active_language] ?? "";
    $[2] = active_language;
    $[3] = destination_types;
    $[4] = tenant?.id;
    $[5] = values;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const example = t3;
  let t4;
  if ($[7] !== active_language) {
    t4 = getOutpostSnippetLanguage(active_language);
    $[7] = active_language;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const snippet_language = t4;
  const maskable_value = useOutpostSnippetMaskableValue();
  let t5;
  if ($[9] !== destination_types || $[10] !== topic_options) {
    t5 = <DestinationCreateFields destination_types={destination_types} topic_options={topic_options} />;
    $[9] = destination_types;
    $[10] = topic_options;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  let t6;
  if ($[12] !== example || $[13] !== maskable_value || $[14] !== snippet_language || $[15] !== t5) {
    t6 = <CodeSnippetWithForm code={example} language={snippet_language} default_open={true} section_title="Create destination properties" maskable_value={maskable_value}>{t5}</CodeSnippetWithForm>;
    $[12] = example;
    $[13] = maskable_value;
    $[14] = snippet_language;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = {
      t: 6
    };
    $[17] = t7;
  } else {
    t7 = $[17];
  }
  let t8;
  if ($[18] !== active_language) {
    t8 = active_language === "http" ? "Copy cURL request and send the API call via your terminal or HTTP client..." : `Run ${capitalizeFirstLetter(active_language)} command to create the destination...`;
    $[18] = active_language;
    $[19] = t8;
  } else {
    t8 = $[19];
  }
  let t9;
  if ($[20] !== destination || $[21] !== t8) {
    t9 = <Div m={t7}><CreatedResourceData waiting_label={t8} status_label="Destination created" created_resource={destination} /></Div>;
    $[20] = destination;
    $[21] = t8;
    $[22] = t9;
  } else {
    t9 = $[22];
  }
  let t10;
  if ($[23] !== t6 || $[24] !== t9) {
    t10 = <Form>{t6}{t9}</Form>;
    $[23] = t6;
    $[24] = t9;
    $[25] = t10;
  } else {
    t10 = $[25];
  }
  return t10;
};
const CreateDestination: React.FC<OutpostGetStartedStepProps> = t0 => {
  const $ = _c(20);
  const {
    context
  } = t0;
  const {
    HookdeckAPI
  } = useContext(GlobalContext);
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = APIMethodKeys.outpost.destinationTypes.list();
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== HookdeckAPI.outpost.destinationTypes) {
    t2 = () => HookdeckAPI.outpost.destinationTypes.list();
    $[1] = HookdeckAPI.outpost.destinationTypes;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const {
    data: destination_types
  } = useSWR(t1, t2);
  const {
    test_url
  } = context;
  let t3;
  if ($[3] !== context.destination?.config.url || $[4] !== context.topics?.length || $[5] !== destination_types || $[6] !== test_url?.ingest_url) {
    const default_destination_type = destination_types?.find(_temp2) ?? destination_types?.[0];
    t3 = getCreateDestinationInitialValues({
      destination_type: default_destination_type,
      topics: context.topics?.length ? ["*"] : undefined,
      destination_url: context.destination?.config.url,
      default_destination_url: test_url?.ingest_url ?? DEFAULT_TEST_DESTINATION_URL
    });
    $[3] = context.destination?.config.url;
    $[4] = context.topics?.length;
    $[5] = destination_types;
    $[6] = test_url?.ingest_url;
    $[7] = t3;
  } else {
    t3 = $[7];
  }
  const initial_values = t3;
  if (!destination_types) {
    let t4;
    if ($[8] === Symbol.for("react.memo_cache_sentinel")) {
      t4 = <Loading />;
      $[8] = t4;
    } else {
      t4 = $[8];
    }
    return t4;
  }
  let t4;
  if ($[9] !== test_url) {
    t4 = test_url && <StyledCard m={{
      b: 4
    }}><StyledCardSection p={4} flex={{
        align: "flex-start",
        gap: 3
      }}><Badge subtle={true} p={0.5} icon="bolt" /><Div flex={{
          direction: "column",
          grow: true
        }}><Text bold={true} m={{
            b: 1
          }}>Test URL ready</Text><Text muted={true} m={{
            b: 3
          }}>We pre-filled the destination URL with a Hookdeck test URL so you can publish a real event without standing up an endpoint.</Text><Link primary={true} small={true} href={test_url.inspect_url} target="_blank" rel="noreferrer" icon="export_notes">View received events in Console</Link></Div></StyledCardSection></StyledCard>;
    $[9] = test_url;
    $[10] = t4;
  } else {
    t4 = $[10];
  }
  let t5;
  if ($[11] !== context || $[12] !== destination_types) {
    t5 = <CreateDestinationForm context={context} destination_types={destination_types} />;
    $[11] = context;
    $[12] = destination_types;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  let t6;
  if ($[14] !== t4 || $[15] !== t5) {
    t6 = <>{t4}{t5}</>;
    $[14] = t4;
    $[15] = t5;
    $[16] = t6;
  } else {
    t6 = $[16];
  }
  let t7;
  if ($[17] !== initial_values || $[18] !== t6) {
    t7 = <Formik initialValues={initial_values} enableReinitialize={true} onSubmit={noopOutpostFormSubmit}>{t6}</Formik>;
    $[17] = initial_values;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  return t7;
};
export default CreateDestination;
function _temp(topic) {
  return {
    value: topic,
    label: topic
  };
}
function _temp2(destination_type) {
  return destination_type.type === "webhook";
}